import { useCallback, useEffect, useState } from "react"
import { graphql, useFragment } from "react-relay"
import { cn } from "@/lib/utils"
import { appointmentServiceLevelAgreementFragment$key } from "@/modules/waiting-room/components/appointment-table/__generated__/appointmentServiceLevelAgreementFragment.graphql"

const appointmentServiceLevelAgreementFragmentDescriptor = graphql`
  fragment appointmentServiceLevelAgreementFragment on WaitingRoomAppointment {
    id
    serviceLevelAgreementThresholds {
      infoUntil
      warningUntil
    }
  }
`

interface AppointmentServiceLevelAgreementProps extends React.ComponentProps<"div"> {
  serviceLevelAgreementThresholdsFragment: appointmentServiceLevelAgreementFragment$key
}

export const AppointmentServiceLevelAgreement = ({
  serviceLevelAgreementThresholdsFragment,
  className,
  ...divProps
}: AppointmentServiceLevelAgreementProps) => {
  const { serviceLevelAgreementThresholds } = useFragment<appointmentServiceLevelAgreementFragment$key>(
    appointmentServiceLevelAgreementFragmentDescriptor,
    serviceLevelAgreementThresholdsFragment,
  )

  const [serviceLevelAgreementMode, setServiceLevelAgreementMode] = useState<"NONE" | "INFO" | "WARNING">("NONE")

  const updateServiceLevelAgreementMode = useCallback(() => {
    if (!serviceLevelAgreementThresholds) {
      setServiceLevelAgreementMode("NONE")
      return
    }

    const infoUntilDateTime = new Date(serviceLevelAgreementThresholds.infoUntil)
    const warningUntilDateTime = new Date(serviceLevelAgreementThresholds.warningUntil)

    const now = new Date()

    let mode: "NONE" | "INFO" | "WARNING" = "NONE"
    if (now <= infoUntilDateTime) {
      mode = "INFO"
    } else if (now <= warningUntilDateTime) {
      mode = "WARNING"
    }
    setServiceLevelAgreementMode(mode)
  }, [serviceLevelAgreementThresholds])

  useEffect(() => {
    updateServiceLevelAgreementMode()
    const interval = setInterval(() => {
      updateServiceLevelAgreementMode()
    }, 1000)

    return () => clearInterval(interval)
  }, [updateServiceLevelAgreementMode])

  const bgClass = (() => {
    switch (serviceLevelAgreementMode) {
      case "INFO":
        return "bg-success"
      case "WARNING":
        return "fast-pulse bg-critical"
      case "NONE":
        return ""
    }
  })()

  return (
    <div {...divProps} className={className}>
      {serviceLevelAgreementMode !== "NONE" && (
        <div className={cn("mx-auto h-2 w-2 justify-center rounded-full", bgClass)}></div>
      )}
    </div>
  )
}
